import { useMutation, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import { ApiError } from './http';

interface FieldErrorSetter {
  (name: string, error: { message: string }): void;
}

export interface UseApiMutationOptions<TData, TVariables> extends UseMutationOptions<TData, ApiError, TVariables> {
  /** Normalmente `form.setError` do react-hook-form (faça o cast pro tipo genérico). Mapeia `ApiError.fieldErrors` (422) direto pros campos. */
  setFieldError?: FieldErrorSetter;
}

/**
 * `useMutation` fino que já mapeia erros de campo (422) pro formulário via `setFieldError`,
 * mantendo o restante do erro (401/403/409/429/500) disponível em `error` pro chamador
 * decidir a mensagem (toast, banner, etc). Reutilizável em qualquer mutation da aplicação.
 */
export function useApiMutation<TData, TVariables>(
  options: UseApiMutationOptions<TData, TVariables>,
): UseMutationResult<TData, ApiError, TVariables> {
  const { setFieldError, onError, ...rest } = options;
  return useMutation<TData, ApiError, TVariables>({
    ...rest,
    onError: (error, variables, onMutateResult, context) => {
      if (setFieldError && error instanceof ApiError && error.code === 'VALIDATION_FAILED') {
        for (const [path, message] of Object.entries(error.fieldErrors)) {
          setFieldError(path, { message });
        }
      }
      onError?.(error, variables, onMutateResult, context);
    },
  });
}
